import React, { useEffect, useState } from 'react';

import { useLocation } from 'react-router-dom';

import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle, CheckCircle, Mail, MapPin, MessageCircle, Phone } from 'lucide-react';

import EmergencyContact from '../components/EmergencyContact';
import FAQ from '../components/FAQ';
import FormField from '../components/ui/FormField';
import FormPhoneInput from '../components/ui/FormPhoneInput';
import FormTextarea from '../components/ui/FormTextarea';
import ModernButton from '../components/ui/ModernButton';
import ModernCard from '../components/ui/ModernCard';
import { useForm } from '../hooks/useForm';
import { useRecaptcha } from '../hooks/useRecaptcha';
import { api } from '../services/api';
import { analytics } from '../utils/analytics';
import { ContactFormValues, contactFormSchema } from '../utils/validation/schemas';

function Contact() {
  const location = useLocation();
  const { verify } = useRecaptcha();
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const initialValues: ContactFormValues = {
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  };

  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleChange,
    handleBlur,
    setFieldValue,
    handleSubmit,
    resetForm,
  } = useForm<ContactFormValues>({
    initialValues,
    validationSchema: contactFormSchema,
    onSubmit: async formValues => {
      try {
        setSubmitStatus('idle');
        setErrorMessage('');

        // Track submission attempt
        analytics.trackEvent({
          action: 'contact_form_submit_attempt',
          category: 'contact',
          label: formValues.subject,
        });

        // Execute reCAPTCHA
        const token = await verify('contact_submit');

        // Submit to API
        await api.contact.submit({
          ...formValues,
          recaptchaToken: token,
        });

        // Success handling
        setSubmitStatus('success');
        analytics.trackEvent({
          action: 'contact_form_success',
          category: 'contact',
          label: formValues.subject,
        });
        resetForm();

        // Scroll to success message
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (error: any) {
        console.error('Submission error:', error);
        setSubmitStatus('error');
        setErrorMessage(
          error.message ||
            'Ha ocurrido un error al enviar tu mensaje. Por favor, intenta nuevamente.'
        );

        analytics.trackEvent({
          action: 'contact_form_error',
          category: 'contact',
          label: error.message,
        });
      }
    },
  });

  useEffect(() => {
    if (location.state) {
      const { subject, message } = location.state as { subject: string; message: string };
      let formattedMessage = message;

      // Format plan details if they exist
      if (location.state.planDetails) {
        const { name, features, category } = location.state.planDetails;
        formattedMessage = `Estoy interesado en obtener más información sobre el siguiente plan:\n\n`;
        formattedMessage += `Plan: ${name}\n`;
        formattedMessage += `Categoría: ${category}\n\n`;
        formattedMessage += `Características principales:\n`;
        features.forEach((feature: string) => {
          formattedMessage += `• ${feature}\n`;
        });
        formattedMessage += `\nMe gustaría recibir información detallada sobre:\n`;
        formattedMessage += `- Costos y condiciones del plan\n`;
        formattedMessage += `- Tiempo de implementación\n`;
        formattedMessage += `- Proceso de inicio y siguientes pasos`;
      }

      setFieldValue('subject', subject || '');
      setFieldValue('message', formattedMessage || '');
    }
  }, [location, setFieldValue]);

  return (
    <div className="py-12 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Contáctanos</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Estamos listos para ayudarte a transformar tu negocio con soluciones tecnológicas a
            medida
          </p>
        </motion.div>

        {/* Sección de Contacto de Emergencia */}
        <div className="mb-12">
          <EmergencyContact />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {/* Información de Contacto */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <ModernCard className="h-full">
              <h2 className="text-2xl font-bold mb-6">Información de Contacto</h2>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="bg-blue-100 p-3 rounded-lg mr-4">
                    <Mail className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1">Email</h3>
                    <p className="text-gray-600">contacto@solinprimejc.com</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-blue-100 p-3 rounded-lg mr-4">
                    <Phone className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1">Teléfono</h3>
                    <p className="text-gray-600">+56 9 8491 8725</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-blue-100 p-3 rounded-lg mr-4">
                    <MapPin className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1">Dirección</h3>
                    <p className="text-gray-600">
                      AV. PDTE. KENNEDY 7440 OFICINA 701
                      <br />
                      VITACURA, SANTIAGO
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-blue-100 p-3 rounded-lg mr-4">
                    <MessageCircle className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1">Horario de Atención</h3>
                    <p className="text-gray-600">
                      Lunes a Viernes: 9:00 - 18:00
                      <br />
                      Sábado: 9:00 - 13:00
                    </p>
                  </div>
                </div>
              </div>
            </ModernCard>
          </motion.div>

          {/* Formulario de Contacto */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-2"
          >
            <ModernCard>
              <h2 className="text-2xl font-bold mb-6">Envíanos un Mensaje</h2>

              <AnimatePresence mode="wait">
                {submitStatus === 'success' ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className="bg-green-50 border border-green-200 text-green-800 rounded-lg p-8 text-center"
                  >
                    <div className="flex justify-center mb-4">
                      <CheckCircle className="h-16 w-16 text-green-500" />
                    </div>
                    <h3 className="text-2xl font-bold mb-2">¡Mensaje Enviado!</h3>
                    <p className="mb-8 text-lg">
                      Gracias por contactarnos. Hemos recibido tu mensaje y nos pondremos en
                      contacto contigo a la brevedad.
                    </p>
                    <ModernButton
                      variant="accent"
                      size="lg"
                      onClick={() => setSubmitStatus('idle')}
                      className="mx-auto"
                    >
                      Enviar otro mensaje
                    </ModernButton>
                  </motion.div>
                ) : (
                  <motion.div
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    {submitStatus === 'error' && (
                      <div className="mb-6 bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 flex items-start">
                        <AlertCircle className="h-5 w-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold">Error al enviar</h4>
                          <p>{errorMessage}</p>
                        </div>
                      </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <FormField
                          label="Nombre"
                          name="name"
                          value={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.name}
                          touched={touched.name}
                          placeholder="Tu nombre completo"
                          required
                        />
                        <FormField
                          label="Email"
                          name="email"
                          type="email"
                          value={values.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.email}
                          touched={touched.email}
                          placeholder="tu@email.com"
                          required
                        />
                      </div>

                      <FormPhoneInput
                        label="Teléfono"
                        value={values.phone}
                        onChange={value => setFieldValue('phone', value)}
                        error={errors.phone}
                        touched={touched.phone}
                      />

                      <FormField
                        label="Asunto"
                        name="subject"
                        value={values.subject}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={errors.subject}
                        touched={touched.subject}
                        placeholder="¿En qué podemos ayudarte?"
                        required
                      />

                      <FormTextarea
                        label="Mensaje"
                        name="message"
                        value={values.message}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={errors.message}
                        touched={touched.message}
                        placeholder="Cuéntanos más sobre tu proyecto o consulta..."
                        rows={6}
                        required
                      />

                      <div className="flex justify-end pt-4">
                        <ModernButton
                          variant="accent"
                          size="lg"
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full sm:w-auto"
                        >
                          {isSubmitting ? (
                            <span className="flex items-center">
                              <svg
                                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                              </svg>
                              Enviando...
                            </span>
                          ) : (
                            'Enviar Mensaje'
                          )}
                        </ModernButton>
                      </div>

                      <p className="text-xs text-gray-500 text-center mt-4">
                        Este sitio está protegido por reCAPTCHA y se aplican la{' '}
                        <a
                          href="https://policies.google.com/privacy"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          Política de Privacidad
                        </a>{' '}
                        y los{' '}
                        <a
                          href="https://policies.google.com/terms"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          Términos de Servicio
                        </a>{' '}
                        de Google.
                      </p>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>
            </ModernCard>
          </motion.div>
        </div>

        {/* FAQs Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mb-16"
        >
          <FAQ className="" />
        </motion.div>

        {/* Map Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <ModernCard className="p-0 overflow-hidden">
            <div className="aspect-w-16 aspect-h-9 h-[400px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3330.1097927339703!2d-70.5752501!3d-33.4184842!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x9662cf3f3e9e0f41%3A0x2e7a8a6b2f7f3c2a!2sAv.%20Pdte.%20Kennedy%207440%2C%20Vitacura%2C%20Regi%C3%B3n%20Metropolitana!5e0!3m2!1ses!2scl!4v1648132133358!5m2!1ses!2scl"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen={true}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Ubicación de la oficina"
              ></iframe>
            </div>
          </ModernCard>
        </motion.div>
      </div>
    </div>
  );
}

export default Contact;
